// Simple "select a record to delete" dropdown for the record-based sections
// (vehicle / coverage / interested party) when the board's "Delete" action is
// chosen. Mirrors the driver Delete flow (a single dropdown, no checkboxes).

import React from "react";
import { Grid } from "@mui/material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
} from "../../../../../../../../components/formsComponents";
import { IAutomobilePolicyChangeProps } from "../../Steps/AutomobilePolicyChange.types";
import { useAutomobilePolicyVehcileInfo } from "../../../hooks/useAutomobileVehicleInfo";
import { useAutomobileCoverageInfo } from "../../../hooks/useAutomobileCoverage";
import { useAutomobileVehicleInterested } from "../../../hooks/useAutomobileVehicleInterested";
import { RecordSectionKey } from "./RecordPanelHost";

interface IDeletePickerV2Props extends IAutomobilePolicyChangeProps {
  section: Exclude<RecordSectionKey, "driverChange">;
}

const CONFIG: Record<
  IDeletePickerV2Props["section"],
  { label: string; field: string }
> = {
  vehicleChange: {
    label: "Select Vehicle to Delete",
    field: "vehicle_change.delete_vehicle",
  },
  coverageChange: {
    label: "Select Coverage to Delete",
    field: "coverage_change.delete_coverage",
  },
  interestedPartyChange: {
    label: "Select Interested Party to Delete",
    field: "interested_party_change.delete_interested_party",
  },
};

export const DeletePickerV2: React.FC<IDeletePickerV2Props> = ({
  section,
  values,
  handleChange,
  customerPolicyId,
}) => {
  const { vehcileDropdown } = useAutomobilePolicyVehcileInfo(customerPolicyId);
  const { coverageDropdown } = useAutomobileCoverageInfo(customerPolicyId);
  const { interestedDropdown } =
    useAutomobileVehicleInterested(customerPolicyId);

  const options =
    section === "vehicleChange"
      ? vehcileDropdown
      : section === "coverageChange"
      ? coverageDropdown
      : interestedDropdown;

  const { label, field } = CONFIG[section];

  const currentValue =
    section === "vehicleChange"
      ? values.vehicle_change.delete_vehicle
      : section === "coverageChange"
      ? values.coverage_change.delete_coverage
      : values.interested_party_change.delete_interested_party;

  return (
    <Grid container>
      <Grid item xs={12} md={6}>
        <CustomFormLabel>{label}</CustomFormLabel>
        <ControlledCustomSelect
          variant="outlined"
          size="small"
          type="string"
          name={field}
          fullWidth
          displayEmpty
          value={currentValue ?? ""}
          onChange={handleChange}
          placeholder="Select One"
          options={options}
          // Rendered inside the V2 right-side drawer (zIndex 1400); the menu
          // portals to the body at the default modal zIndex (1300) and would
          // open behind the drawer. Keep it above the drawer.
          MenuProps={{ sx: { zIndex: 1600 } }}
        />
      </Grid>
    </Grid>
  );
};
